import { Fragment, useEffect, useState } from 'react';
import { useSocket } from '../hooks/useSocket';
import {
  AdminOrderItem,
  FilterOptions,
  OrderSelectionOptionItem,
  OrderStatusKeys,
  SortOrderType,
} from '../interface/interface';
import { OrderList } from '../components/OrderList';
import { useAuth } from 'react-oidc-context';
import { PaginationState } from '@tanstack/react-table';
import Loading from '../components/Loading';
import {
  getAllOrderStatues,
  getOrderListForAdmin,
  updateOrderStatus,
} from '../service';
import showToast from '../components/ToastComponent';
import moment from 'moment';

function AdminOrders() {
  const { user } = useAuth();
  const [newOrders, isSocketConnected] = useSocket(
    user?.access_token as string,
  );
  const [orders, setOrders] = useState<Array<AdminOrderItem>>([]);

  /**
   * The pagination states should be declared here to ensure that the
   * API Communication will not affect the socket ones.
   */
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 1000,
  });

  const [totalRecords, setTotalRecords] = useState<number>(0);

  // Search
  const [search, setSearch] = useState<string>('');
  const [submitSearch, setSubmission] = useState<number>(0);

  // OrderStatusList
  const [orderStatusLoading, setOrderStatusLoad] = useState<boolean>(false);
  const [defaultSelectedStatus, setDefaultSelection] = useState<
    Array<OrderSelectionOptionItem>
  >([]);
  const [orderStatusList, setOrderStatusList] = useState<
    Array<OrderSelectionOptionItem>
  >([]);

  // Update Order Products
  const [, setOrderStatusLoadUpdate] = useState<boolean>(false);
  const [statusKeys, setOrderStatusKeys] = useState<OrderStatusKeys>();
  const [refershTrigger, setRefreshTrigger] = useState<number>(0);
  const [paginationCount, setTotalPaginationCount] = useState<number>(0);
  const [submitCount, setSubmitCount] = useState<number>(0);

  const [childLoad, setChildLoad] = useState<boolean>(false);

  const [filterDate, setFilterDate] = useState<{
    from: Date | null;
    to: Date | null;
    filterBy?: string;
  }>();

  const [sort, setSort] = useState<SortOrderType>(
    (localStorage.getItem('sortOrder') as SortOrderType) || 'ASC',
  );

  const [filterBy] = useState(
    localStorage.getItem('filterBy') || FilterOptions.CreatedDate,
  );

  const [filterApplied, setFilterApplyStatus] = useState<boolean>(false);

  const [firstOrderFilter, setFirstOrderFilter] = useState<boolean>(false);
  const [guestOrderFilter, setGuestOrderFilter] = useState<boolean>(false);

  useEffect(() => {
    if (!user?.access_token) return;

    setOrderStatusLoad(true);
    getAllOrderStatues(user.access_token).then((data) => {
      if (typeof data === 'number') return;
      setOrderStatusList(data);
      setOrderStatusLoad(false);
    });
  }, []);

  const filterRecord = () => {
    const selectedStatuses: OrderSelectionOptionItem[] = [];
    const savedValues = localStorage.getItem('filterStatuses')
      ? JSON.parse(localStorage.getItem('filterStatuses') ?? '[]')
      : [];

    if (savedValues.length) {
      for (const savedValue of savedValues) {
        selectedStatuses.push(savedValue);
      }
      return selectedStatuses;
    }
    return defaultSelectedStatus;
  };

  useEffect(() => {
    if (!user?.access_token) return;

    setChildLoad(true);
    setOrders([]);
    getOrderListForAdmin(user.access_token, {
      page: pagination.pageIndex,
      limit: pagination.pageSize,
      search,
      filter: filterRecord()?.map((df) => df.id),
      filterDateFromDate: localStorage.getItem('fromDate')
        ? moment(localStorage.getItem('fromDate')).toDate()
        : (filterDate?.from as Date),
      filterDateToDate: localStorage.getItem('toDate')
        ? moment(localStorage.getItem('toDate')).toDate()
        : (filterDate?.to as Date),
      filterBy: localStorage.getItem('filterBy') ?? filterBy,
      sort: (localStorage.getItem('sortOrder') as SortOrderType) || sort,
    }).then((data): void => {
      console.log('D> Received Data. Time to Render!!', data);
      if (typeof data === 'number') {
        console.log('Unable to retrieve the order list');
        return;
      }
      setOrders(data.data);
      setTotalRecords(data.total);
      setChildLoad(false);

      if (
        paginationCount !== Math.ceil(data.total / pagination.pageSize) &&
        submitCount < submitSearch
      ) {
        setTotalPaginationCount(Math.ceil(data.total / pagination.pageSize));
        setSubmitCount(submitSearch);
        setPagination({
          pageIndex: 0,
          pageSize: pagination.pageSize,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    submitSearch,
    defaultSelectedStatus,
    refershTrigger,
    filterApplied,
    sort,
  ]);

  useEffect(() => {
    if (!user?.access_token || !statusKeys) return;
    setOrderStatusLoadUpdate(true);
    updateOrderStatus(user.access_token, statusKeys).then((data) => {
      if (typeof data === 'number') return;

      if (data?.status === 200) {
        setOrderStatusKeys(undefined);
        setRefreshTrigger((prev) => prev + 1);
        setOrderStatusLoadUpdate(false);
      } else {
        showToast({
          message: `Unable to update: ${data?.data?.message}`,
          type: 'warning',
        });
      }
    });
  }, [statusKeys]);

  useEffect(() => {
    if (!newOrders.length) return;

    const filterNewOrders = newOrders.filter((order) => {
      return filterRecord()?.some(
        (status) => status.id === order.orderStatus.id,
      );
    });

    setOrders((prev) =>
      [
        ...(filterRecord().length ? filterNewOrders : newOrders),
        ...prev,
      ]?.filter(
        (item: AdminOrderItem, index: number, self: AdminOrderItem[]) =>
          index === self.findIndex((t) => t.invoiceId === item.invoiceId),
      ),
    );
  }, [newOrders]);

  return (
    <Fragment>
      {isSocketConnected && !orderStatusLoading ? (
        <OrderList
          orderItem={orders}
          updateOrderStatus={(orderData: OrderStatusKeys) =>
            setOrderStatusKeys(orderData)
          }
          pagination={{
            state: pagination,
            setter: setPagination,
            totalRecords,
          }}
          search={{
            value: search,
            changeHandler: setSearch,
            submitSearch: setSubmission,
          }}
          select={{
            loading: orderStatusLoading,
            data: orderStatusList,
            selected: defaultSelectedStatus.length
              ? defaultSelectedStatus
              : filterRecord(),
            selectHandler: (el) => {
              localStorage.setItem('filterStatuses', JSON.stringify(el));
              setDefaultSelection(el);
            },
          }}
          setFilterDate={setFilterDate}
          setFilterApplyStatus={setFilterApplyStatus}
          setSortOrder={setSort}
          sort={sort}
          filterBy={filterBy}
          firstOrderFilter={firstOrderFilter}
          setFirstOrderFilter={setFirstOrderFilter}
          guestOrderFilter={guestOrderFilter}
          setGuestOrderFilter={setGuestOrderFilter}
          childLoader={childLoad}
        />
      ) : (
        <Loading />
      )}
    </Fragment>
  );
}

export default AdminOrders;
